import { createApi } from '@reduxjs/toolkit/query/react';

import { apiFormsTemplates } from '~constants/api';
import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';
import {
  CreateTemplateRequest,
  CreateTemplateResponse,
  CreateVersionResponse,
  FormTemplate,
  FormTemplateVersion,
  PublishTemplateResponse,
  UpdateDraftVersionRequest,
  UpdateTemplateRequest,
} from '~forms/types';

export const formTemplatesApi = createApi({
  reducerPath: 'formTemplatesApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiFormsTemplates }),
  tagTypes: ['formTemplate', 'formTemplateList'],
  endpoints: (builder) => ({
    getTemplates: builder.query<FormTemplate[], void>({
      query: () => ({ url: '', method: 'GET' }),
      transformResponse: (payload) => unwrap<FormTemplate[]>(payload),
      providesTags: [{ type: 'formTemplateList', id: 'LIST' }],
    }),

    getTemplate: builder.query<FormTemplate, string>({
      query: (id) => ({ url: `/${id}`, method: 'GET' }),
      transformResponse: (payload) => unwrap<FormTemplate>(payload),
      providesTags: (_result, _error, id) => [{ type: 'formTemplate', id }],
    }),

    createTemplate: builder.mutation<CreateTemplateResponse, CreateTemplateRequest>({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
      }),
      transformResponse: (payload) => unwrap<CreateTemplateResponse>(payload),
      invalidatesTags: [{ type: 'formTemplateList', id: 'LIST' }],
    }),

    updateTemplate: builder.mutation<FormTemplate, { id: string } & UpdateTemplateRequest>({
      query: ({ id, ...body }) => ({
        url: `/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (payload) => unwrap<FormTemplate>(payload),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'formTemplate', id },
        { type: 'formTemplateList', id: 'LIST' },
      ],
    }),

    updateDraftVersion: builder.mutation<FormTemplateVersion, UpdateDraftVersionRequest>({
      query: ({ id, versionId, field_schema }) => ({
        url: `/${id}/versions/${versionId}`,
        method: 'PUT',
        body: { field_schema },
      }),
      transformResponse: (payload) => unwrap<FormTemplateVersion>(payload),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'formTemplate', id }],
    }),

    publishTemplate: builder.mutation<PublishTemplateResponse, string>({
      query: (id) => ({
        url: `/${id}/publish`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (payload) => unwrap<PublishTemplateResponse>(payload),
      invalidatesTags: (_result, _error, id) => [
        { type: 'formTemplate', id },
        { type: 'formTemplateList', id: 'LIST' },
      ],
    }),

    createVersion: builder.mutation<CreateVersionResponse, string>({
      query: (id) => ({
        url: `/${id}/versions`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (payload) => unwrap<CreateVersionResponse>(payload),
      invalidatesTags: (_result, _error, id) => [
        { type: 'formTemplate', id },
        { type: 'formTemplateList', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetTemplatesQuery,
  useGetTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useUpdateDraftVersionMutation,
  usePublishTemplateMutation,
  useCreateVersionMutation,
} = formTemplatesApi;
